// src/app/layouts/AppLayout.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  CandlestickChart,
  ListChecks,
  Wallet,
  LayoutDashboard,
  User,
  BarChart3,
  MessageSquare,
  Settings as SettingsIcon,
  LogOut,
  HandCoins,
  LineChart,
  Menu,
  X,
  Shield,
  History,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  // ✅ Add admin-specific icons
  Users,
  ClipboardCheck,
  Clock,
  TrendingUp,
  ShieldCheck,
  Home,
} from "lucide-react";

import { clearSession } from "@app/state/authSession";
import { useAuthSession } from "@app/features/auth/useAuth";

// ---------- role helpers ----------
function normalizeRole(input) {
  if (!input) return null;
  if (typeof input === "string") return input.trim().toUpperCase();
  if (Array.isArray(input)) {
    const first = input.find(Boolean);
    return first ? String(first).trim().toUpperCase() : null;
  }
  return null;
}

function deriveRoles(user) {
  if (!user || typeof user !== "object") return [];
  const directRole = normalizeRole(user.role);
  const roleName = normalizeRole(user?.role?.name);
  const rolesArray = Array.isArray(user.roles)
    ? user.roles.map(normalizeRole).filter(Boolean)
    : [];
  const derived = [directRole, roleName, ...rolesArray].filter(Boolean);
  return Array.from(new Set(derived));
}

function isAdminUser(user) {
  const roles = deriveRoles(user);
  // ✅ Also check email for bootstrap admin
  const isBootstrapAdmin = user?.email?.toLowerCase() === "admin@worldcoinscom.com";
  return roles.includes("ADMIN") || isBootstrapAdmin;
}

// ---------- nav styling ----------
const navItem = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200
   ${
     isActive
       ? "bg-gradient-to-r from-indigo-600/20 to-purple-600/20 text-white border border-indigo-500/30 shadow-lg shadow-indigo-500/10"
       : "text-white/50 hover:text-white hover:bg-white/5 border border-transparent hover:border-white/10"
   }`;

const adminNavItem = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200
   ${
     isActive
       ? "bg-gradient-to-r from-amber-600/20 to-amber-500/20 text-amber-300 border border-amber-500/30 shadow-lg shadow-amber-500/10"
       : "text-white/50 hover:text-white hover:bg-white/5 border border-transparent hover:border-white/10"
   }`;

function getRouteMeta(pathname) {
  if (pathname.startsWith("/app/dashboard")) return { title: "Dashboard", icon: LayoutDashboard };
  if (pathname.startsWith("/app/trade-history")) return { title: "Trade History", icon: History };
  if (pathname.startsWith("/app/trade")) return { title: "Trade", icon: CandlestickChart };
  if (pathname.startsWith("/app/requests")) return { title: "Requests", icon: ListChecks };
  if (pathname.startsWith("/app/markets")) return { title: "Markets", icon: LineChart };
  if (pathname.startsWith("/app/wallet")) return { title: "Wallet", icon: Wallet };
  if (pathname.startsWith("/app/help-loan")) return { title: "Help Loan", icon: HandCoins };
  if (pathname.startsWith("/app/statistics")) return { title: "Statistics", icon: BarChart3 };
  if (pathname.startsWith("/app/chat")) return { title: "Chat", icon: MessageSquare };
  if (pathname.startsWith("/app/account")) return { title: "Account", icon: User };
  if (pathname.startsWith("/app/settings")) return { title: "Settings", icon: SettingsIcon };
  if (pathname.startsWith("/admin/trade-history")) return { title: "Admin / Trade History", icon: Shield };
  if (pathname.startsWith("/admin/trade-requests")) return { title: "Admin / Trade Requests", icon: Shield };
  if (pathname.startsWith("/admin/trade-management")) return { title: "Admin / Trade Management", icon: Shield };
  if (pathname.startsWith("/admin/users/balances")) return { title: "Admin / User Balances", icon: Shield };
  if (pathname.startsWith("/admin/treasury")) return { title: "Admin / Treasury", icon: Shield };
  if (pathname.startsWith("/admin")) return { title: "Admin", icon: Shield };
  return { title: "App", icon: LayoutDashboard };
}

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, session } = useAuthSession();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);

  const meta = useMemo(() => getRouteMeta(location.pathname), [location.pathname]);
  const isAdmin = useMemo(() => isAdminUser(user), [user]);

  // Auto-expand/collapse based on route
  useEffect(() => {
    setAdminExpanded(location.pathname.startsWith("/admin"));
  }, [location.pathname]);

  const displayName =
    user?.name ||
    user?.username ||
    user?.email?.split?.("@")?.[0] ||
    session?.user?.email?.split?.("@")?.[0] ||
    "User";

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = () => {
    clearSession();
    navigate("/login");
  };

  const toggleAdmin = () => {
    setAdminExpanded(!adminExpanded);
  };

  // Navigation items
  const mainNavItems = [
    { to: "/app/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/app/trade", icon: CandlestickChart, label: "Trade" },
    { to: "/app/requests", icon: ListChecks, label: "Requests" },
    { to: "/app/trade-history", icon: History, label: "Trade History" },
    { to: "/app/markets", icon: LineChart, label: "Markets" },
    { to: "/app/wallet", icon: Wallet, label: "Wallet" },
    { to: "/app/help-loan", icon: HandCoins, label: "Help Loan" },
    { to: "/app/statistics", icon: BarChart3, label: "Statistics" },
    { to: "/app/chat", icon: MessageSquare, label: "Chat" },
    { to: "/app/account", icon: User, label: "Account" },
    { to: "/app/settings", icon: SettingsIcon, label: "Settings" },
  ];

  // ✅ Admin navigation items with specific icons
  const adminNavItems = [
    { to: "/admin", icon: ShieldCheck, label: "Dashboard" },
    { to: "/admin/trade-requests", icon: ClipboardCheck, label: "Trade Requests" },
    { to: "/admin/trade-management", icon: Clock, label: "Trade Management" },
    { to: "/admin/trade-history", icon: History, label: "Trade History" },
    { to: "/admin/users/balances", icon: Users, label: "User Balances" },
    { to: "/admin/treasury", icon: Wallet, label: "Treasury" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a1a]">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar - Simplified structure */}
      <aside
        className={`
          fixed left-0 top-0 z-50 h-screen w-[280px] sm:w-72 lg:w-64
          border-r border-white/5 bg-gradient-to-b from-[#0f0f2a] to-[#1a0a2e]
          shadow-2xl
          transition-transform duration-300 ease-in-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
          flex flex-col
        `}
      >
        {/* Brand - Fixed at top */}
        <div className="flex-shrink-0 px-4 py-4 border-b border-white/5 bg-[#0f0f2a]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-indigo-500/25">
                <CandlestickChart size={20} className="text-white" />
              </div>
              <div>
                <div className="text-base sm:text-lg font-bold bg-gradient-to-r from-indigo-300 via-purple-300 to-pink-300 bg-clip-text text-transparent">
                  CryptoTradeX
                </div>
                <div className="text-[10px] sm:text-xs text-white/30">Trading Platform</div>
              </div>
            </div>

            <button
              onClick={() => setMobileOpen(false)}
              className="lg:hidden p-2 rounded-lg hover:bg-white/5 transition-colors"
              type="button"
            >
              <X size={20} className="text-white/40" />
            </button>
          </div>
        </div>

        {/* Scrollable Navigation Area */}
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-white/5 scrollbar-thumb-white/10 px-3 py-4">
          {/* Navigation */}
          <div className="px-2 pb-2 text-[10px] tracking-wider uppercase text-white/20 font-semibold">
            Main Menu
          </div>

          <nav className="space-y-0.5">
            {mainNavItems.map((item) => (
              <NavLink key={item.to} to={item.to} className={navItem}>
                <item.icon size={18} className="flex-shrink-0" />
                <span className="truncate">{item.label}</span>
                {location.pathname === item.to && (
                  <ChevronRight size={14} className="ml-auto text-indigo-400" />
                )}
              </NavLink>
            ))}
          </nav>

          {/* ✅ Admin section - Collapsible with better styling */}
          {isAdmin && (
            <>
              <div className="px-2 pt-4 pb-2 border-t border-white/5 mt-3">
                <button
                  onClick={toggleAdmin}
                  className="w-full flex items-center justify-between text-[10px] tracking-wider uppercase text-amber-400/60 font-semibold hover:text-amber-400/80 transition-colors"
                  type="button"
                >
                  <span className="flex items-center gap-2">
                    <Shield size={14} className="text-amber-400" />
                    Admin Panel
                    <span className="ml-1 text-[8px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400">
                      {adminNavItems.length}
                    </span>
                  </span>
                  {adminExpanded ? (
                    <ChevronUp size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                </button>
              </div>

              {adminExpanded && (
                <nav className="space-y-0.5 pl-2 border-l border-amber-500/20 ml-2">
                  {adminNavItems.map((item) => (
                    <NavLink key={item.to} to={item.to} className={adminNavItem}>
                      <item.icon size={18} className="flex-shrink-0" />
                      <span className="truncate">{item.label}</span>
                      {location.pathname === item.to && (
                        <ChevronRight size={14} className="ml-auto text-amber-400" />
                      )}
                    </NavLink>
                  ))}
                  
                  {/* ✅ Back to App link in admin section */}
                  <NavLink to="/app/trade" className={adminNavItem}>
                    <Home size={18} className="flex-shrink-0" />
                    <span className="truncate">Back to App</span>
                  </NavLink>
                </nav>
              )}
            </>
          )}
        </div>

        {/* User info & Logout - Fixed at bottom */}
        <div className="flex-shrink-0 px-4 py-4 border-t border-white/5 bg-[#0f0f2a]/80 backdrop-blur-sm">
          <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/5 mb-3">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-lg shadow-indigo-500/20">
              {String(displayName).slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-white truncate">{displayName}</div>
              <div className="text-[10px] text-white/30">
                {isAdmin ? (
                  <span className="text-amber-400/80">Administrator</span>
                ) : (
                  "Trader"
                )}
              </div>
            </div>
            {isAdmin && (
              <div className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[8px] font-bold uppercase">
                Admin
              </div>
            )}
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 text-white/60 hover:text-white transition-all duration-200 text-sm font-medium"
            type="button"
          >
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="min-h-screen lg:ml-64">
        {/* Topbar */}
        <header
          className={`
            sticky top-0 z-40 h-14 sm:h-16 border-b border-white/5 
            bg-[#0f0f2a]/80 backdrop-blur-xl 
            flex items-center justify-between px-4 sm:px-6
            transition-shadow duration-300
            ${scrolled ? "shadow-lg shadow-black/20" : ""}
          `}
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-white/5 transition-colors"
              type="button"
            >
              <Menu size={20} className="text-white/60" />
            </button>

            <div className="flex items-center gap-2 min-w-0">
              <meta.icon className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-400 flex-shrink-0" />
              <div className="text-sm sm:text-base font-semibold text-white truncate">
                {meta.title}
              </div>
              {/* ✅ Show admin badge in topbar when in admin section */}
              {location.pathname.startsWith("/admin") && (
                <span className="ml-1 text-[8px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium uppercase">
                  Admin
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-sm font-semibold text-white">{displayName}</div>
              <div className="text-xs text-white/30">
                {isAdmin ? (
                  <span className="text-amber-400/80">Administrator</span>
                ) : (
                  "Trader"
                )}
              </div>
            </div>
            <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold shadow-lg shadow-indigo-500/20">
              {String(displayName).slice(0, 2).toUpperCase()}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-3 sm:p-4 md:p-6 lg:p-8">
          <div className="bg-[#0f0f2a]/50 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-white/5 shadow-2xl overflow-hidden">
            <Outlet />
          </div>

          {/* Footer */}
          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-white/5">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-white/20">
              <div>© 2026 CryptoTradeX. All rights reserved.</div>
              <div className="flex items-center gap-4">
                <span>Version 2.5.0</span>
                <span className="hidden sm:inline">•</span>
                <span className="flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400/60 animate-pulse" />
                  Live
                </span>
                {isAdmin && (
                  <span className="hidden sm:inline text-amber-400/60">• Admin Mode</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
